import { User as SupabaseAuthUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { User as AppUser } from '../models/User';

export type { AppUser as User };

export class UserService {
  static async getCurrentUser(uid: string): Promise<AppUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, phone_number, address, emergency_contact, role, created_at, updated_at, org_id')
      .eq('id', uid)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    // Map database snake_case fields to User model camelCase fields
    return {
      uid: data.id,
      email: data.email || '',
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      phoneNumber: data.phone_number || '',
      address: data.address || {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        apartment: '',
        unit: '',
      },
      role: data.role || 'member',
      createdAt: data.created_at || new Date().toISOString(),
      updatedAt: data.updated_at || new Date().toISOString(),
      ...(data.org_id && { orgId: data.org_id }),
      ...(data.emergency_contact && { emergencyContact: data.emergency_contact }),
    } as AppUser;
  }

  static async getUserById(uid: string): Promise<AppUser | null> {
    return this.getCurrentUser(uid);
  }

  static async createUser(user: AppUser): Promise<void> {
    const { error } = await supabase.from('users').insert({
      id: user.uid,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      phone_number: user.phoneNumber,
      address: user.address,
      role: user.role,
      org_id: user.orgId,
      created_at: user.createdAt,
      updated_at: user.updatedAt,
    });

    if (error) {
      throw error;
    }
  }

  static async updateUser(uid: string, data: Partial<AppUser>): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone_number: data.phoneNumber,
        address: data.address,
        emergency_contact: data.emergencyContact,
        role: data.role,
        org_id: data.orgId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', uid);

    if (error) {
      throw error;
    }
  }

  /**
   * Fetch the profile row for an auth user; insert a default row if the
   * profile is missing. Used by `AuthContext` on session resolution.
   */
  static async getOrCreateProfile(authUser: SupabaseAuthUser): Promise<AppUser> {
    const existing = await this.getCurrentUser(authUser.id);
    if (existing) {
      return existing;
    }

    const now = new Date().toISOString();
    const { data: inserted, error } = await supabase
      .from('users')
      .insert({
        id: authUser.id,
        email: authUser.email || '',
        first_name: '',
        last_name: '',
        phone_number: '',
        address: { street: '', city: '', state: '', postalCode: '', country: '', apartment: '', unit: '' },
        role: 'member',
        org_id: null,
        created_at: now,
        updated_at: now,
      })
      .select('id, email, first_name, last_name, phone_number, address, role, created_at, updated_at, org_id')
      .single();

    if (error || !inserted) {
      throw error || new Error('Failed to create user profile');
    }

    return {
      uid: inserted.id,
      email: inserted.email || authUser.email || '',
      firstName: inserted.first_name || '',
      lastName: inserted.last_name || '',
      phoneNumber: inserted.phone_number || '',
      address: inserted.address || { street: '', city: '', state: '', postalCode: '', country: '', apartment: '', unit: '' },
      role: inserted.role || 'member',
      createdAt: inserted.created_at || now,
      updatedAt: inserted.updated_at || now,
      ...(inserted.org_id && { orgId: inserted.org_id }),
    } as AppUser;
  }
}
